import type { Howl } from 'howler';
import type { AudioLifecycle } from './audio-lifecycle';

export type BingoSounds = {
    ui?: Howl;
    announcer?: Howl;
    ball30?: Howl;
    ball75?: Howl;
    ball80?: Howl;
    ball90?: Howl;
};

export type StoppablePromise<T> = Promise<T> & {
    stop(): void;
};

export type SoundPlayer = {
    play: (key: keyof BingoSounds, id: string) => StoppablePromise<void>;
    playLoop: (key: keyof BingoSounds, id: string) => () => void;
    mute: (isMuted: boolean) => void;
    initialized: boolean;
};

export const DEFAULT_SOUND_PLAYER: SoundPlayer = {
    play: () => {
        const promise = Promise.resolve() as StoppablePromise<void>;
        promise.stop = () => {};
        return promise;
    },
    playLoop: () => () => {},
    mute: () => {},
    initialized: false,
};

export const createSoundPlayer = (
    howlers: BingoSounds,
    lifecycle: AudioLifecycle
): SoundPlayer => {
    // Returns the Howl only when a call into it would start playback now.
    // Dropped requests are never saved for replay; an unavailable context
    // just triggers context-only recovery for the next fresh request.
    const getPlayableHowl = (key: keyof BingoSounds) => {
        if (!lifecycle.isPlaybackReady()) {
            lifecycle.recover();
            return;
        }

        const howler = howlers[key];
        if (!howler || howler.state() !== 'loaded') {
            return;
        }

        return howler;
    };

    return {
        initialized: true,
        play(key, id) {
            const howler = getPlayableHowl(key);
            if (!howler) return DEFAULT_SOUND_PLAYER.play(key, id);

            let stop = () => {};

            const promise = new Promise<void>((resolve) => {
                const soundId = howler.play(id);
                howler.on('end', function onEnd($soundId) {
                    if (soundId === $soundId) {
                        resolve();
                        howler.off('end', onEnd);
                    }
                });

                stop = () => {
                    howler.stop(soundId);
                };
            }) as StoppablePromise<void>;

            promise.stop = () => {
                stop();
            };

            return promise;
        },
        playLoop(key, id) {
            const howler = getPlayableHowl(key);
            if (!howler) return DEFAULT_SOUND_PLAYER.playLoop(key, id);

            const soundId = howler.play(id);
            howler.loop(true, soundId);

            return () => {
                howler.loop(false, soundId);
                howler.stop(soundId);
            };
        },
        mute(isMuted) {
            for (const key in howlers) {
                const howler = howlers[key as keyof BingoSounds];
                if (howler) {
                    howler.mute(isMuted);
                }
            }
        },
    };
};
